import {APIurl} from "./Utils";
import firebase from "firebase/compat/app";
import {AwardModel, Experience, ExperienceTask, Flow, FlowForTrigger, FlowsTitle, GeocacheCatalogEntry, GeocacheType, LiveMonitorModel, Organization, PaymentQuote, PaymentQuoteRecipientSplit, PaymentRecipient, PlayerModel, Preventivo, QrCodeCatalogEntry, RewardRuleModel, StickerModel, TranslationEntry, UserLocation, UserModel} from "./Model";

export const getExperiences = async (): Promise<any[]> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken()
        const res = await fetch(`${APIurl}/getExperiences`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nella richiesta");
        }

        const data = await res.json();
        return data.experiences; // oppure data.experiences se hai un wrapper nel backend
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return [];
    }
};

export const duplicateExperience = async (id: string): Promise<string> => {
    const token = await firebase.auth().currentUser?.getIdToken();
    const res = await fetch(`${APIurl}/duplicateExperience/${id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error((await res.json()).message || "Errore duplicazione");
    return (await res.json()).id;
};

export const createExperience = async (): Promise<string | null> => {
    try {
        const currentUser = firebase.auth().currentUser;
        const token = await currentUser?.getIdToken();

        if (!token || !currentUser?.uid) {
            throw new Error("Utente non autenticato");
        }

        const res = await fetch(`${APIurl}/createExperience`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ created_by: currentUser.uid })
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nella creazione dell’esperienza");
        }

        const data = await res.json();
        return data.id; // restituisce l’id creato
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
}

export const updateFlow = async (
    experienceId: string,
    flows: any[]
): Promise<boolean> => {
    try {
        const currentUser = firebase.auth().currentUser;
        const token = await currentUser?.getIdToken();

        if (!token || !currentUser?.uid) {
            throw new Error("Utente non autenticato");
        }

        const res = await fetch(`${APIurl}/updateFlow/${experienceId}`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ flows })
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nell’aggiornamento del flow");
        }

        return true; // successo
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const getFlows = async (experienceId: string): Promise<Flow[] | null> => {
    try {
        const currentUser = firebase.auth().currentUser;
        const token = await currentUser?.getIdToken();

        if (!token || !currentUser?.uid) {
            throw new Error("Utente non autenticato");
        }

        const res = await fetch(`${APIurl}/getFlows/${experienceId}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nel recupero dei flow");
        }

        const data = await res.json();

        // Spostiamo i campi di data direttamente nello step
        const flowsWithFlattenedData = data.map((flow: any) => ({
            ...flow,
            steps: flow.steps.map((step: any) => ({
                ...step,
                ...step.data,  // destrutturiamo i dati extra fuori da step.data
                data: undefined // opzionale: rimuoviamo il campo data
            }))
        }));

        return flowsWithFlattenedData;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

export const editExperience = async (
    id: string,
    body: Record<string, any>
): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken()
        const res = await fetch(`${APIurl}/editExperience/${id}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(body),
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante l’aggiornamento");
        }

        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const getExperience = async (id: string): Promise<{ experience: Experience, organizations: Organization[] } | null> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken()
        const res = await fetch(`${APIurl}/getExperience/${id}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!res.ok) throw new Error("Errore nel recupero dell'esperienza");

        const data = await res.json();
        return data;
    } catch (error: any) {
        alert(error.message);
        return null;
    }
};

export const deleteExperience = async (id: string): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken()
        const res = await fetch(`${APIurl}/deleteExperience/${id}`, {
            method: "DELETE",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!res.ok) throw new Error("Errore durante l'eliminazione");

        return true;
    } catch (error: any) {
        alert(error.message);
        return false;
    }
};

export const markExperienceAsDeleted = async (id: string): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken()
        const res = await fetch(`${APIurl}/markExperienceDeleted/${id}`, {
            method: "PATCH",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!res.ok) throw new Error("Errore durante l'aggiornamento dello stato");

        return true;
    } catch (error: any) {
        alert(error.message);
        return false;
    }
};

export const markExperienceAsDraft = async (id: string): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken()
        const res = await fetch(`${APIurl}/markExperienceDraft/${id}`, {
            method: "PATCH",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!res.ok) throw new Error("Errore durante l'aggiornamento dello stato");

        return true;
    } catch (error: any) {
        alert(error.message);
        return false;
    }
};

export const markExperienceAsPublic = async (id: string): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken()
        const res = await fetch(`${APIurl}/markExperiencePublic/${id}`, {
            method: "PATCH",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!res.ok) throw new Error("Errore durante l'aggiornamento dello stato");

        return true;
    } catch (error: any) {
        alert(error.message);
        return false;
    }
};

export const createOrganization = async (organization: {
    name: string;
    stripe_public_key?: string;
    stripe_secret_key?: string;
}): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();

        const res = await fetch(`${APIurl}/createOrganization`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(organization),
        });

        if (!res.ok) {
            const error = await res.json();
            throw new Error(error.message || "Errore durante la creazione");
        }

        return true;
    } catch (err: any) {
        alert(err.message);
        return false;
    }
};

export const editOrganization = async (
    id: string,
    body: {
        name: string;
        stripe_public_key?: string;
        stripe_secret_key?: string;
    }
): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();

        const res = await fetch(`${APIurl}/editOrganization/${id}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(body),
        });

        if (!res.ok) {
            const error = await res.json();
            throw new Error(error.message || "Errore durante l'aggiornamento");
        }

        return true;
    } catch (err: any) {
        alert(err.message);
        return false;
    }
};

export const deleteOrganization = async (id: string): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();

        const res = await fetch(`${APIurl}/deleteOrganization/${id}`, {
            method: "DELETE",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const error = await res.json();
            throw new Error(error.message || "Errore durante l'eliminazione");
        }

        return true;
    } catch (err: any) {
        alert(err.message);
        return false;
    }
};

export const getOrganizations = async (): Promise<any[]> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken()
        const res = await fetch(`${APIurl}/getOrganizations`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nella richiesta");
        }

        const data = await res.json();
        return data.organizations; // oppure data.organizations se hai un wrapper nel backend
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return [];
    }
};

export const getUserLocations = async (experienceId: string): Promise<UserLocation[] | null> => {
    try {
        const currentUser = firebase.auth().currentUser;
        const token = await currentUser?.getIdToken();
        const uid = currentUser?.uid;

        if (!token || !uid) {
            throw new Error("Utente non autenticato");
        }

        const res = await fetch(`${APIurl}/getUserLocation/${uid}/${experienceId}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nel recupero delle posizioni");
        }

        const data = await res.json();

        // Mappiamo i dati per garantire tipi coerenti
        const locations: UserLocation[] = data.map((item: any) => ({
            id: item.id,
            name: item.name,
            latitude: parseFloat(item.latitude),
            longitude: parseFloat(item.longitude),
            date: item.date,
        }));

        return locations;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

export const getLiveMonitor = async (experienceId: string): Promise<LiveMonitorModel[] | null> => {
    try {
        const currentUser = firebase.auth().currentUser;
        const token = await currentUser?.getIdToken();

        if (!token) {
            throw new Error("Utente non autenticato");
        }

        const res = await fetch(`${APIurl}/getLiveMonitor/${experienceId}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nel recupero delle posizioni");
        }

        const data = await res.json();
        return data;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

/**
 * Carica un file nella cartella media del server
 * @param file - File da caricare
 */
export const uploadFile = async (file: File): Promise<{ message: string; filename: string } | null> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const formData = new FormData();
        formData.append("file", file);

        const res = await fetch(`${APIurl}/media/uploadFile`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
            body: formData
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nel caricamento del file");
        }

        return await res.json();
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

/**
 * Ottiene la lista dei file presenti nella cartella media
 */
export const listFiles = async (): Promise<string[]> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();

        const res = await fetch(`${APIurl}/media/listFiles`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nella lettura della lista file");
        }

        const data = await res.json();
        return data.files || [];
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return [];
    }
};

/**
 * Scarica un file dalla cartella media protetta dal token
 * @param filename - Nome del file da scaricare
 * @returns Blob del file scaricato, oppure null in caso di errore
 */
export const getFile = async (filename: string): Promise<Blob | null> => {
    try {
        const currentUser = firebase.auth().currentUser;
        if (!currentUser) throw new Error("Utente non autenticato");

        const token = await currentUser.getIdToken();

        const res = await fetch(`${APIurl}/media/getFile/${encodeURIComponent(filename)}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            // Provo a leggere il messaggio di errore dal server
            let errMsg = "Errore nel download del file";
            try {
                const errData = await res.json();
                errMsg = errData.message || errMsg;
            } catch {}
            throw new Error(errMsg);
        }

        // Restituisco il blob del file
        return await res.blob();
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

/**
 * Elimina un file dalla cartella media protetta dal token
 * @param filename - Nome del file da eliminare
 * @returns true se il file è stato eliminato correttamente, false altrimenti
 */
export const deleteFile = async (filename: string): Promise<boolean> => {
    try {
        const currentUser = firebase.auth().currentUser;
        if (!currentUser) throw new Error("Utente non autenticato");

        const token = await currentUser.getIdToken();

        const res = await fetch(`${APIurl}/media/deleteFile/${encodeURIComponent(filename)}`, {
            method: "DELETE",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            let errMsg = "Errore nell'eliminazione del file";
            try {
                const errData = await res.json();
                errMsg = errData.message || errMsg;
            } catch {}
            throw new Error(errMsg);
        }

        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};


export const getPlayers = async (experienceId: string): Promise<{players: PlayerModel[], flows: FlowsTitle[]} | null> => {
    try {
        const currentUser = firebase.auth().currentUser;
        const token = await currentUser?.getIdToken();

        if (!token || !currentUser?.uid) {
            throw new Error("Utente non autenticato");
        }

        const res = await fetch(`${APIurl}/getPlayers/${experienceId}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nel recupero dei partecipanti");
        }

        return await res.json();
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

export const addPlayer = async (experienceId: string, userId: string, flowId?: string): Promise<boolean> => {
    try {
        const currentUser = firebase.auth().currentUser;
        const token = await currentUser?.getIdToken();

        if (!token || !currentUser?.uid) {
            throw new Error("Utente non autenticato");
        }

        console.log(JSON.stringify({ userId, flowId }))
        const res = await fetch(`${APIurl}/addPlayer/${experienceId}`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ userId, flowId })
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nell'aggiunta del partecipante");
        }

        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const deletePlayer = async (id: string, experienceId: string): Promise<boolean> => {
    try {
        const currentUser = firebase.auth().currentUser;
        const token = await currentUser?.getIdToken();

        if (!token || !currentUser?.uid) {
            throw new Error("Utente non autenticato");
        }

        const res = await fetch(`${APIurl}/deletePlayer/${id}/${experienceId}`, {
            method: "DELETE",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nella rimozione del partecipante");
        }

        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const togglePlayerLeader = async (id: string, experienceId: string): Promise<boolean> => {
    try {
        const currentUser = firebase.auth().currentUser;
        const token = await currentUser?.getIdToken();

        if (!token || !currentUser?.uid) {
            throw new Error("Utente non autenticato");
        }
        const res = await fetch(`${APIurl}/togglePlayerLeader/${id}/${experienceId}`, {
            method: "PATCH",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nel cambio capogruppo");
        }

        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const updatePlayers = async (experienceId: string, players: any[]): Promise<boolean> => {
    try {
        const currentUser = firebase.auth().currentUser;
        const token = await currentUser?.getIdToken();

        if (!token || !currentUser?.uid) {
            throw new Error("Utente non autenticato");
        }

        const res = await fetch(`${APIurl}/updatePlayers/${experienceId}`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ players })
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nell'aggiornamento dei partecipanti");
        }

        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const getUsers = async (): Promise<UserModel[]> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken()
        const res = await fetch(`${APIurl}/getUsers`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nella richiesta");
        }

        const data = await res.json();
        return data.users;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return [];
    }
};

export const getUsersNotifications = async (experienceId: string): Promise<UserModel[]> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken()
        const res = await fetch(`${APIurl}/getUsersNotifications/${experienceId}`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nella richiesta");
        }

        const data = await res.json();
        return data.users;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return [];
    }
};

export const inviteUsers = async (
    experienceId: string,
    users: { email: string; firstName?: string; lastName?: string }[]
): Promise<{ message: string; invited: { email: string; userId: string }[] }> => {
    const currentUser = firebase.auth().currentUser;
    const token = await currentUser?.getIdToken();

    if (!token || !currentUser?.uid) {
        throw new Error("Utente non autenticato");
    }

    const res = await fetch(`${APIurl}/inviteUsers/${experienceId}`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(users)
    });

    if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Errore nell'invito degli utenti");
    }

    return res.json(); // 👈 ritorna { message, invited }
};

export const sendEmails = async (
    recipients: string[],
    title: string,
    desc: string
): Promise<{}> => {
    const currentUser = firebase.auth().currentUser;
    const token = await currentUser?.getIdToken();

    if (!token || !currentUser?.uid) {
        throw new Error("Utente non autenticato");
    }

    const res = await fetch(`${APIurl}/experiences/sendEmails`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            recipients: recipients,
            title: title,
            desc: desc
        })
    });

    if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Errore nell'invio delle mail");
    }

    return res.json(); // 👈 ritorna { message, invited }
};

export const sendPushNotifications = async (
    recipients: string[],
    title: string,
    desc: string
): Promise<{}> => {
    const currentUser = firebase.auth().currentUser;
    const token = await currentUser?.getIdToken();

    if (!token || !currentUser?.uid) {
        throw new Error("Utente non autenticato");
    }

    const res = await fetch(`${APIurl}/experiences/sendPushNotifications`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            recipients: recipients,
            title: title,
            desc: desc
        })
    });

    if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Errore nell'invio delle notifiche");
    }

    return res.json(); // 👈 ritorna { message, invited }
};

// ── Awards (Premi) ──────────────────────────────────────────────────────────
// All on /app/v1/awards — admin-gated server-side (requireAdmin), same
// Firebase token as every other admin call here.

export const addAwards = async (
    recipients: string[],
    title: string,
    description: string,
    validUntil: string
): Promise<{}> => {
    const currentUser = firebase.auth().currentUser;
    const token = await currentUser?.getIdToken();

    if (!token || !currentUser?.uid) {
        throw new Error("Utente non autenticato");
    }

    const res = await fetch(`${APIurl}/app/v1/awards/bulk`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            recipients: recipients,
            title: title,
            description: description,
            validUntil: validUntil
        })
    });

    if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Errore nella creazione dei premi");
    }

    return res.json();
};

export const getAwards = async (): Promise<AwardModel[]> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken()
        const res = await fetch(`${APIurl}/app/v1/awards`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
            },
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nella richiesta");
        }

        const data = await res.json();
        return data;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return [];
    }
};

export const createAward = async (
    user_id: string,
    title: string,
    description: string,
    valid_until?: string
): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/app/v1/awards`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({
                user_id,
                title,
                description,
                valid_until: valid_until ?? null
            }),
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nella richiesta");
        }

        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const updateAward = async (
    id: string,
    title: string,
    description: string,
    valid_until?: string
): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/app/v1/awards/${id}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({
                title,
                description,
                valid_until: valid_until ?? null
            }),
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore nella richiesta");
        }

        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const deleteUser = async (userId: string): Promise<boolean> => {
    try {
        const currentUser = firebase.auth().currentUser;
        const token = await currentUser?.getIdToken();

        if (!token || !currentUser?.uid) {
            throw new Error("Utente non autenticato");
        }

        const res = await fetch(`${APIurl}/deleteUser/${userId}`, {
            method: "DELETE",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante l'eliminazione dell'utente");
        }

        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const deleteAward = async (id: string): Promise<boolean> => {
    try {
        const currentUser = firebase.auth().currentUser;
        const token = await currentUser?.getIdToken();

        if (!token || !currentUser?.uid) {
            throw new Error("Utente non autenticato");
        }

        const res = await fetch(`${APIurl}/app/v1/awards/${id}`, {
            method: "DELETE",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante l'eliminazione del premio");
        }

        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

// ── Experience reward rules (Premi & Sticker automatici) ───────────────────────

export interface RewardRuleInput {
    reward_type: "AWARD" | "STICKER";
    scope: "INDIVIDUAL_RANK" | "GROUP_RANK" | "ALL_PARTICIPANTS";
    rank?: number | null;
    sticker_id?: string | null;
    award_title?: string;
    award_description?: string;
    award_valid_until?: string | null;
    award_valid_days?: number | null;
    is_active?: boolean;
}

export const getExperienceRewardRules = async (
    experienceId: string
): Promise<{ rules: RewardRuleModel[]; rewardsAssignedAt: string | null }> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/app/v1/experience-rewards/${experienceId}`, {
            headers: { "Authorization": `Bearer ${token}` },
        });
        if (!res.ok) throw new Error((await res.json()).message || "Errore");
        return await res.json();
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return { rules: [], rewardsAssignedAt: null };
    }
};

export const createRewardRule = async (experienceId: string, input: RewardRuleInput): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/app/v1/experience-rewards/${experienceId}`, {
            method: "POST",
            headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(input),
        });
        if (!res.ok) throw new Error((await res.json()).message || "Errore");
        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const updateRewardRule = async (id: string, input: RewardRuleInput): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/app/v1/experience-rewards/rule/${id}`, {
            method: "PUT",
            headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(input),
        });
        if (!res.ok) throw new Error((await res.json()).message || "Errore");
        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const deleteRewardRule = async (id: string): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/app/v1/experience-rewards/rule/${id}`, {
            method: "DELETE",
            headers: { "Authorization": `Bearer ${token}` },
        });
        if (!res.ok) throw new Error((await res.json()).message || "Errore");
        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const runExperienceRewardAssignment = async (
    experienceId: string,
    force: boolean = false
): Promise<{ ran: boolean; reason?: string; rulesApplied?: number } | null> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/app/v1/experience-rewards/${experienceId}/run`, {
            method: "POST",
            headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ force }),
        });
        if (!res.ok) throw new Error((await res.json()).message || "Errore");
        return await res.json();
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

export const resetBaseTimers = async (experienceId: string): Promise<boolean> => {
    try {
        const currentUser = firebase.auth().currentUser;
        const token = await currentUser?.getIdToken();

        if (!token || !currentUser?.uid) {
            throw new Error("Utente non autenticato");
        }

        const res = await fetch(`${APIurl}/resetBaseTimers/${experienceId}`, {
            method: "DELETE",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante il reset dei base timers");
        }

        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const resetPoints = async (experienceId: string): Promise<boolean> => {
    try {
        const currentUser = firebase.auth().currentUser;
        const token = await currentUser?.getIdToken();

        if (!token || !currentUser?.uid) {
            throw new Error("Utente non autenticato");
        }

        const res = await fetch(`${APIurl}/resetPoints/${experienceId}`, {
            method: "DELETE",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante il reset dei punti");
        }

        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const resetGameData = async (experienceId: string): Promise<boolean> => {
    try {
        const currentUser = firebase.auth().currentUser;
        const token = await currentUser?.getIdToken();

        if (!token || !currentUser?.uid) {
            throw new Error("Utente non autenticato");
        }

        const res = await fetch(`${APIurl}/admin/v1/reset/${experienceId}`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        });

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante il reset dei dati di gioco");
        }

        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

// ─── Stickers ────────────────────────────────────────────────────────────────

export const getStickers = async (): Promise<StickerModel[]> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/stickers`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error((await res.json()).message || 'Errore');
        return (await res.json()).stickers;
    } catch (err: any) { alert(`Errore: ${err.message}`); return []; }
};

export const getUserStickers = async (userId: string): Promise<StickerModel[]> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/stickers/user/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error((await res.json()).message || 'Errore');
        return (await res.json()).stickers;
    } catch (err: any) { alert(`Errore: ${err.message}`); return []; }
};

export const createSticker = async (title: string, description: string, img_url: string): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/stickers`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ title, description, img_url }),
        });
        if (!res.ok) throw new Error((await res.json()).message || 'Errore');
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

export const updateSticker = async (id: string, title: string, description: string, img_url: string): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/stickers/${id}`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ title, description, img_url }),
        });
        if (!res.ok) throw new Error((await res.json()).message || 'Errore');
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

export const deleteSticker = async (id: string): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/stickers/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error((await res.json()).message || 'Errore');
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

export const assignSticker = async (userId: string, stickerId: string): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/stickers/assign`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId, stickerId }),
        });
        if (!res.ok) throw new Error((await res.json()).message || 'Errore');
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

export const unassignSticker = async (userId: string, stickerId: string): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/stickers/unassign/${userId}/${stickerId}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error((await res.json()).message || 'Errore');
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

// ─── App Settings ────────────────────────────────────────────────────────────

export interface AppSettings {
    android: string;
    ios: string;
    maintenance: boolean;
}

export const getSettings = async (): Promise<AppSettings | null> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/getSettings`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error((await res.json()).message || 'Errore');
        return await res.json();
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return null;
    }
};

export const setAppVersion = async (device: 'android' | 'ios', value: string): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/setAppVersion/${device}`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ value }),
        });
        if (!res.ok) throw new Error((await res.json()).message || 'Errore');
        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export interface DashboardStats {
    total_users: number;
    total_experiences: number;
    public_experiences: number;
    draft_experiences: number;
    total_organizations: number;
    total_stickers: number;
}

export const getDashboardStats = async (): Promise<DashboardStats | null> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/getDashboardStats`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error((await res.json()).message || 'Errore');
        return await res.json();
    } catch (err: any) {
        console.error(err);
        return null;
    }
};

export const adminEditUser = async (
    userId: string,
    data: {
        firstname?: string;
        lastname?: string;
        email: string;
        phone?: string;
        birthdate?: string;
        gender?: string;
        marketing_consent?: boolean;
    }
): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/editUser/${userId}`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        if (!res.ok) throw new Error((await res.json()).message || 'Errore');
        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const updateUserExperiences = async (userId: string, experienceIds: string[]): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/updateUserExperiences/${userId}`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ experiences_managed: experienceIds }),
        });
        if (!res.ok) throw new Error((await res.json()).message || 'Errore');
        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

export const setMaintenance = async (active: boolean): Promise<boolean> => {
    try {
        const token = await firebase.auth().currentUser?.getIdToken();
        const res = await fetch(`${APIurl}/admin/setMaintenance`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ active }),
        });
        if (!res.ok) throw new Error((await res.json()).message || 'Errore');
        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

// ── Task System ───────────────────────────────────────────────────────────────

async function taskAuthFetch(path: string, options: RequestInit = {}): Promise<any> {
    const token = await firebase.auth().currentUser?.getIdToken();
    const res = await fetch(`${APIurl}/admin/v1/tasks${path}`, {
        ...options,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        },
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Errore');
    }
    return res.json();
}

export const getTaskFlows = async (experienceId: string): Promise<FlowForTrigger[]> => {
    try { return await taskAuthFetch(`/flows/${experienceId}`, { method: 'GET' }); }
    catch (err: any) { alert(`Errore: ${err.message}`); return []; }
};

export const getTasks = async (experienceId: string): Promise<ExperienceTask[]> => {
    try { return await taskAuthFetch(`/experience/${experienceId}`, { method: 'GET' }); }
    catch (err: any) { alert(`Errore: ${err.message}`); return []; }
};

export const createTask = async (
    experienceId: string,
    payload: Omit<ExperienceTask, 'id' | 'experience_id' | 'created_at' | 'updated_at'>
): Promise<string | null> => {
    try {
        const data = await taskAuthFetch(`/experience/${experienceId}`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        return data.id;
    } catch (err: any) { alert(`Errore: ${err.message}`); return null; }
};

export const updateTask = async (
    taskId: string,
    payload: Omit<ExperienceTask, 'id' | 'experience_id' | 'created_at' | 'updated_at'>
): Promise<boolean> => {
    try {
        await taskAuthFetch(`/${taskId}`, { method: 'PUT', body: JSON.stringify(payload) });
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

export const deleteTask = async (taskId: string): Promise<boolean> => {
    try {
        await taskAuthFetch(`/${taskId}`, { method: 'DELETE' });
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

export const duplicateTask = async (taskId: string): Promise<string | null> => {
    try {
        const data = await taskAuthFetch(`/${taskId}/duplicate`, { method: 'POST' });
        return data.id;
    } catch (err: any) { alert(`Errore: ${err.message}`); return null; }
};

export const reorderTasks = async (
    experienceId: string,
    order: { id: string; task_order: number }[]
): Promise<boolean> => {
    try {
        await taskAuthFetch(`/reorder/${experienceId}`, { method: 'PUT', body: JSON.stringify({ order }) });
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

// ── Translations (dynamic DB strings, IT/EN) ────────────────────────────────

async function translationsAuthFetch(path: string, options: RequestInit = {}): Promise<any> {
    const token = await firebase.auth().currentUser?.getIdToken();
    const res = await fetch(`${APIurl}/admin/v1/translations${path}`, {
        ...options,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        },
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Errore');
    }
    return res.json();
}

export const getTranslations = async (filters: {
    experience_id?: string;
    type?: string;
    status?: string;
    search?: string;
}): Promise<TranslationEntry[]> => {
    try {
        const params = new URLSearchParams();
        if (filters.experience_id) params.set('experience_id', filters.experience_id);
        if (filters.type) params.set('type', filters.type);
        if (filters.status) params.set('status', filters.status);
        if (filters.search) params.set('search', filters.search);
        const qs = params.toString();
        return await translationsAuthFetch(qs ? `/?${qs}` : '/', { method: 'GET' });
    } catch (err: any) { alert(`Errore: ${err.message}`); return []; }
};

export const saveTranslation = async (entry: {
    entity_type: string;
    entity_id: string;
    field_name: string;
    value: string;
}): Promise<boolean> => {
    try {
        await translationsAuthFetch('/', { method: 'PUT', body: JSON.stringify(entry) });
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

// ── Fixed QR / Geocache catalogs ────────────────────────────────────────────────
// Shared by the React admin and the Flutter app's Dev Tools (both hit the
// same /admin/v1 routes, authenticated with the same Firebase token).

async function qrCodesAuthFetch(path: string, options: RequestInit = {}): Promise<any> {
    const token = await firebase.auth().currentUser?.getIdToken();
    const res = await fetch(`${APIurl}/admin/v1/qr-codes${path}`, {
        ...options,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        },
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Errore');
    }
    return res.json();
}

export const getQrCodes = async (params?: { search?: string; unbound?: boolean }): Promise<QrCodeCatalogEntry[]> => {
    try {
        const qs = new URLSearchParams();
        if (params?.search) qs.set('search', params.search);
        if (params?.unbound) qs.set('unbound', '1');
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        return await qrCodesAuthFetch(suffix, { method: 'GET' });
    } catch (err: any) { alert(`Errore: ${err.message}`); return []; }
};

export const createQrCode = async (
    payload: { label?: string; code?: string; lat?: number; lng?: number; organization_id?: string }
): Promise<QrCodeCatalogEntry | null> => {
    try { return await qrCodesAuthFetch('', { method: 'POST', body: JSON.stringify(payload) }); }
    catch (err: any) { alert(`Errore: ${err.message}`); return null; }
};

export const updateQrCode = async (
    id: string,
    payload: { label?: string; code?: string; lat?: number | null; lng?: number | null; organization_id?: string }
): Promise<QrCodeCatalogEntry | null> => {
    try { return await qrCodesAuthFetch(`/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }); }
    catch (err: any) { alert(`Errore: ${err.message}`); return null; }
};

export const deleteQrCode = async (id: string): Promise<boolean> => {
    try { await qrCodesAuthFetch(`/${id}`, { method: 'DELETE' }); return true; }
    catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

async function geocachesAuthFetch(path: string, options: RequestInit = {}): Promise<any> {
    const token = await firebase.auth().currentUser?.getIdToken();
    const res = await fetch(`${APIurl}/admin/v1/geocaches${path}`, {
        ...options,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        },
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Errore');
    }
    return res.json();
}

export const getGeocaches = async (params?: { search?: string; unbound?: boolean }): Promise<GeocacheCatalogEntry[]> => {
    try {
        const qs = new URLSearchParams();
        if (params?.search) qs.set('search', params.search);
        if (params?.unbound) qs.set('unbound', '1');
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        return await geocachesAuthFetch(suffix, { method: 'GET' });
    } catch (err: any) { alert(`Errore: ${err.message}`); return []; }
};

export const createGeocache = async (
    payload: { label: string; description?: string; lat?: number; lng?: number; type?: GeocacheType; code?: string; organization_id?: string }
): Promise<GeocacheCatalogEntry | null> => {
    try { return await geocachesAuthFetch('', { method: 'POST', body: JSON.stringify(payload) }); }
    catch (err: any) { alert(`Errore: ${err.message}`); return null; }
};

export const updateGeocache = async (
    id: string,
    payload: { label?: string; description?: string; lat?: number | null; lng?: number | null; type?: GeocacheType; code?: string; organization_id?: string }
): Promise<GeocacheCatalogEntry | null> => {
    try { return await geocachesAuthFetch(`/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }); }
    catch (err: any) { alert(`Errore: ${err.message}`); return null; }
};

export const deleteGeocache = async (id: string): Promise<boolean> => {
    try { await geocachesAuthFetch(`/${id}`, { method: 'DELETE' }); return true; }
    catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

// ── Preventivi (quote requests) ────────────────────────────────────────────────

async function preventiviAuthFetch(path: string, options: RequestInit = {}): Promise<any> {
    const token = await firebase.auth().currentUser?.getIdToken();
    const res = await fetch(`${APIurl}/admin/v1/preventivi${path}`, {
        ...options,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        },
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Errore');
    }
    return res.json();
}

export const getPreventivi = async (): Promise<Preventivo[]> => {
    try { return await preventiviAuthFetch('', { method: 'GET' }); }
    catch (err: any) { alert(`Errore: ${err.message}`); return []; }
};

export const getPreventivo = async (id: string): Promise<Preventivo | null> => {
    try { return await preventiviAuthFetch(`/${id}`, { method: 'GET' }); }
    catch (err: any) { alert(`Errore: ${err.message}`); return null; }
};

export const updatePreventivo = async (
    id: string,
    payload: Partial<Preventivo>
): Promise<boolean> => {
    try {
        await preventiviAuthFetch(`/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

export const approveEdit = async (id: string): Promise<boolean> => {
    try {
        await preventiviAuthFetch(`/${id}/approve-edit`, { method: 'POST' });
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

export const rejectEdit = async (id: string, note?: string): Promise<boolean> => {
    try {
        await preventiviAuthFetch(`/${id}/reject-edit`, { method: 'POST', body: JSON.stringify({ note }) });
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

// ─────────────────────────────────────────────────────────────────────────────

export const updateLastStep = async (
    participationId: string,
    index: number,
    flowId: string,
    semaforo: string
): Promise<boolean> => {
    try {
        const currentUser = firebase.auth().currentUser;
        const token = await currentUser?.getIdToken();

        if (!token || !currentUser?.uid) {
            throw new Error("Utente non autenticato");
        }

        const res = await fetch(
            `${APIurl}/updateLastStep/${participationId}/${index}/${flowId}/${semaforo}`,
            {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );

        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.message || "Errore durante l'aggiornamento");
        }

        return true;
    } catch (err: any) {
        alert(`Errore: ${err.message}`);
        return false;
    }
};

// ── Preventivi di Pagamento (Stripe Connect split-payment quotes) ──────────────
// Sistema separato da getPreventivi/getPreventivo sopra — non confondere.

async function paymentQuotesAuthFetch(path: string, options: RequestInit = {}): Promise<any> {
    const token = await firebase.auth().currentUser?.getIdToken();
    const res = await fetch(`${APIurl}/payment-quotes/v1/admin${path}`, {
        ...options,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        },
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Errore');
    }
    return res.json();
}

export const getPaymentQuotes = async (): Promise<PaymentQuote[]> => {
    try { return await paymentQuotesAuthFetch('', { method: 'GET' }); }
    catch (err: any) { alert(`Errore: ${err.message}`); return []; }
};

export const getPaymentQuote = async (id: string): Promise<PaymentQuote | null> => {
    try { return await paymentQuotesAuthFetch(`/${id}`, { method: 'GET' }); }
    catch (err: any) { alert(`Errore: ${err.message}`); return null; }
};

export const createPaymentQuote = async (payload: Omit<Partial<PaymentQuote>, 'items'> & { items: any[] }): Promise<string | null> => {
    try {
        const res = await paymentQuotesAuthFetch('', { method: 'POST', body: JSON.stringify(payload) });
        return res.id;
    } catch (err: any) { alert(`Errore: ${err.message}`); return null; }
};

export const updatePaymentQuote = async (id: string, payload: Omit<Partial<PaymentQuote>, 'items'> & { items: any[] }): Promise<boolean> => {
    try {
        await paymentQuotesAuthFetch(`/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

export const sendPaymentQuote = async (id: string): Promise<boolean> => {
    try {
        await paymentQuotesAuthFetch(`/${id}/send`, { method: 'POST' });
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

export const cancelPaymentQuote = async (id: string): Promise<boolean> => {
    try {
        await paymentQuotesAuthFetch(`/${id}/cancel`, { method: 'POST' });
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

export const updatePaymentQuoteSplits = async (id: string, splits: Partial<PaymentQuoteRecipientSplit>[]): Promise<boolean> => {
    try {
        await paymentQuotesAuthFetch(`/${id}/splits`, { method: 'PUT', body: JSON.stringify({ splits }) });
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

async function paymentRecipientsAuthFetch(path: string, options: RequestInit = {}): Promise<any> {
    const token = await firebase.auth().currentUser?.getIdToken();
    const res = await fetch(`${APIurl}/payment-quotes/v1/recipients${path}`, {
        ...options,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        },
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Errore');
    }
    return res.json();
}

export const getPaymentRecipients = async (): Promise<PaymentRecipient[]> => {
    try { return await paymentRecipientsAuthFetch('', { method: 'GET' }); }
    catch (err: any) { alert(`Errore: ${err.message}`); return []; }
};

export const createPaymentRecipient = async (payload: { name: string; stripe_account_id: string; email?: string }): Promise<boolean> => {
    try {
        await paymentRecipientsAuthFetch('', { method: 'POST', body: JSON.stringify(payload) });
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

export const updatePaymentRecipient = async (id: string, payload: Partial<PaymentRecipient>): Promise<boolean> => {
    try {
        await paymentRecipientsAuthFetch(`/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};

export const deletePaymentRecipient = async (id: string): Promise<boolean> => {
    try {
        await paymentRecipientsAuthFetch(`/${id}`, { method: 'DELETE' });
        return true;
    } catch (err: any) { alert(`Errore: ${err.message}`); return false; }
};